import { ErrorResponse, ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import {
    ContactSourceCreatePayload,
    ContactSourceDetailPayload,
    ContactSourceUpdatePayload,
    DataGridHRPayload,
} from '../types/payload'
import {
    AssigneeStats,
    ChannelStats,
    ContactSource,
    ContactSourceStatusStatistic,
    ConversationData,
    ConversionRateStats,
    PendingAlertsStats,
    PeriodStats,
    ProductGroupStats,
    RevenueContactSource,
} from '../types/response'

export const getContactSource = async (
    payload: DataGridPayload & { page_size: number }
): Promise<ResponseArray<ContactSource>> => {
    const response = await datacentral.get(`/contacts?${qs.stringify(payload)}`)
    return response.data
}
export const getContactSourceStatusStatistic = async (
    payload: Omit<DataGridPayload, 'page' | 'page_size'>
): Promise<ResponseObject<ContactSourceStatusStatistic>> => {
    try {
        const queryString = payload ? `?${qs.stringify(payload)}` : ''
        const response = await datacentral.get<
            ResponseObject<ContactSourceStatusStatistic>
        >(`/contacts/statistic${queryString}&q=${payload.q}`)
        return response.data
    } catch (error) {
        console.error('Lỗi khi lấy thống kê trạng thái nguồn liên hệ:', error)
        throw error
    }
}
export const getContactSourceByID = async (
    payload: ContactSourceDetailPayload
) => {
    const response = await datacentral.get<ResponseObject<ContactSource>>(
        `/contacts/${payload.contact_source_id}`
    )
    return response.data
}

export const createContactSource = async (
    payload: ContactSourceCreatePayload
): Promise<ResponseObject<ContactSource> | ErrorResponse> => {
    const response = await datacentral.post(`/contacts`, payload)
    return response.data
}

export const updateContactSource = async (
    payload: Partial<ContactSourceUpdatePayload>
): Promise<ResponseObject<ContactSource> | ErrorResponse> => {
    const response = await datacentral.put(
        `/contacts/${payload.contact_source_id}`,
        payload
    )
    return response.data
}

export const deleteContactSource = async (
    payload: ContactSourceDetailPayload
): Promise<ResponseObject<ContactSource> | ErrorResponse> => {
    const response = await datacentral.delete(
        `/contacts/${payload.contact_source_id}`
    )
    return response.data
}

export const convertToHRContactSource = async (
    payload: ContactSourceDetailPayload
): Promise<ResponseObject<ContactSource> | ErrorResponse> => {
    const response = await datacentral.put(
        `/contacts/${payload.contact_source_id}/humanresources`
    )
    return response.data
}

export const markSpamContactSource = async (
    payload: ContactSourceDetailPayload
): Promise<ResponseObject<ContactSource> | ErrorResponse> => {
    const response = await datacentral.put(
        `/contacts/${payload.contact_source_id}/markspam`
    )
    return response.data
}

export const getHRContactSource = async (
    payload: DataGridHRPayload
): Promise<ResponseArray<ContactSource>> => {
    const response = await datacentral.get(
        `/contacts/humanresources?${qs.stringify(payload)}`
    )
    return response.data
}

export const getRevenueContactSourceByCustomer = async (
    payload: ContactSourceDetailPayload
): Promise<ResponseObject<RevenueContactSource> | ErrorResponse> => {
    const response = await datacentral.get(
        `/contacts/${payload.contact_source_id}/revenue`
    )
    return response.data
}

export const getConversationById = async (conversationId: number) => {
    const response = await datacentral.get<ResponseObject<ConversationData>>(
        `/contacts/conversations/${conversationId}`
    )
    return response.data
}
export const exportContactSource = async (payload: DataGridPayload) => {
    try {
        const queryString = qs.stringify(payload)

        const response = await datacentral.get(
            `/contacts/export?${queryString}`,
            {
                responseType: 'blob',
            }
        )

        return response.data
    } catch (error) {
        console.error('Lỗi khi tải file Excel:', error)
        throw error
    }
}

export const getContactSourceUpdates = async ({
    contactSourceID,
    payload,
}: {
    contactSourceID: number
    payload: Pick<DataGridPayload, 'page' | 'page_size'>
}) => {
    const response = await datacentral.get<ResponseArray<any>>(
        `/contacts/${contactSourceID}/updates?${qs.stringify(payload)}`
    )
    return response?.data
}

export interface EmailExistsResponse {
    email: string
    exists_in_customers: boolean
    exists_in_customer_contacts: boolean
    exists_in_contact_sources: boolean
    exists: boolean
}

export const checkEmailExists = async (
    email: string
): Promise<EmailExistsResponse | null> => {
    try {
        const response = await datacentral.get<
            ResponseObject<EmailExistsResponse>
        >(`/contacts/check-email?email=${encodeURIComponent(email)}`)
        return response?.data?.result || null
    } catch (error) {
        console.error(error)
        return null
    }
}

// mark or unmark following
export const markOrUnmarkFollowing = async (
    payload: ContactSourceDetailPayload
): Promise<ResponseObject<ContactSource> | ErrorResponse> => {
    const response = await datacentral.put(
        `/contacts/${payload.contact_source_id}/mark-or-unmark-following`
    )
    return response.data
}

// Classify lead status (warm-lead, sale-qualified-lead, cool-lead, un-qualified-lead)
export type LeadStatus =
    | 'warm-lead'
    | 'sale-qualified-lead'
    | 'cool-lead'
    | 'un-qualified-lead'

export interface ClassifyLeadPayload {
    contact_source_id: number
    lead_status: LeadStatus
    contact_source_notes?: string
}

export const classifyContactSource = async (
    payload: ClassifyLeadPayload
): Promise<ResponseObject<ContactSource> | ErrorResponse> => {
    const response = await datacentral.put(
        `/contacts/${payload.contact_source_id}/classify`,
        {
            lead_status: payload.lead_status,
            contact_source_notes: payload.contact_source_notes,
        }
    )
    return response.data
}

export interface WebsiteReferrer {
    website_referrer: string
    count: number
}

export const getWebsiteReferrers = async (): Promise<
    ResponseArray<WebsiteReferrer>
> => {
    const response = await datacentral.get<ResponseArray<WebsiteReferrer>>(
        `/contacts/website-referrers`
    )
    return response.data
}

/**
 * Download import template Excel file
 */
export const getContactImportTemplate = async (): Promise<Blob> => {
    const response = await datacentral.get(`/contacts/import/template`, {
        responseType: 'blob',
    })
    return response.data
}

export interface ImportContactResult {
    success_count: number
    error_count: number
    errors: Array<{
        row: number
        email: string
        message: string
    }>
    created_contact_sources: Array<{
        row: number
        contact_source_id: number
        email: string
        name: string
    }>
}

/**
 * Import contact sources from Excel file
 */
export const importContactsFromExcel = async (
    file: File
): Promise<ResponseObject<ImportContactResult>> => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await datacentral.post<
        ResponseObject<ImportContactResult>
    >(`/contacts/import`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })
    return response.data
}

/**
 * Check if a contact source exists by object ID (e.g. livechat room UUID)
 * GET /workers/contacts/:contactSourceObjectID
 */
export const getContactByObjectId = async (
    objectId: string
): Promise<ContactSource | null> => {
    try {
        const response = await datacentral.get<ResponseObject<ContactSource>>(
            `/workers/contacts/${objectId}`
        )
        return response.data?.result || null
    } catch {
        return null
    }
}

/**
 * Patch contact source by object ID (e.g. update status after done/block)
 * PATCH /workers/contacts/:contactSourceObjectID
 */
export const patchContactByObjectId = async (
    objectId: string,
    payload: Partial<ContactSourceUpdatePayload>
): Promise<ContactSource | null> => {
    const response = await datacentral.patch<ResponseObject<ContactSource>>(
        `/workers/contacts/${objectId}`,
        { source: payload }
    )
    return response.data?.result || null
}

// ============== NEW APIS ==============

/**
 * Get leads assigned to current user (based on JWT employee_id)
 * GET /contacts/my-leads
 */
export const getMyLeads = async (
    payload: DataGridPayload & { page_size: number }
): Promise<ResponseArray<ContactSource>> => {
    const response = await datacentral.get(
        `/contacts/my-leads?${qs.stringify(payload)}`
    )
    return response.data
}

/**
 * Get stats by assignee
 * GET /contacts/statistic/by-assignee
 */
// Unified filter payload - same format as list API
export interface StatFilterPayload {
    q?: string
    filters?: string // base64 encoded filters
}

export type StatsByAssigneePayload = StatFilterPayload

export const getStatsByAssignee = async (
    payload?: StatsByAssigneePayload
): Promise<ResponseArray<AssigneeStats>> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ResponseArray<AssigneeStats>>(
        `/contacts/statistic/by-assignee${queryString}`
    )
    return response.data
}

/**
 * Get revenue stats by assignee
 * GET /contacts/statistic/revenue-by-assignee
 */
export interface RevenueByAssigneeStats {
    employee_id: number
    employee_name: string
    employee_email_company: string
    total_leads: number
    converted_leads: number
    total_revenue: number
}

export interface RevenueByAssigneeResponse
    extends ResponseArray<RevenueByAssigneeStats> {
    total_revenue: number
}

export type RevenueByAssigneePayload = StatFilterPayload

export const getRevenueByAssignee = async (
    payload?: RevenueByAssigneePayload
): Promise<RevenueByAssigneeResponse> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<RevenueByAssigneeResponse>(
        `/contacts/statistic/revenue-by-assignee${queryString}`
    )
    return response.data
}

/**
 * Get pending alerts stats
 * GET /contacts/statistic/pending-alerts
 */
export type PendingAlertsStatsPayload = StatFilterPayload

export const getPendingAlertsStats = async (
    payload?: PendingAlertsStatsPayload
): Promise<ResponseObject<PendingAlertsStats>> => {
    const response = await datacentral.get<ResponseObject<PendingAlertsStats>>(
        `/contacts/statistic/pending-alerts`,
        { params: payload }
    )
    return response.data
}

/**
 * Get conversion rate stats
 * GET /contacts/statistic/conversion-rate
 */
export type ConversionRateStatsPayload = StatFilterPayload

export const getConversionRateStats = async (
    payload?: ConversionRateStatsPayload
): Promise<ResponseObject<ConversionRateStats>> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ResponseObject<ConversionRateStats>>(
        `/contacts/statistic/conversion-rate${queryString}`
    )
    return response.data
}

/**
 * Get stats by period (day, week, month)
 * GET /contacts/statistic/by-period
 */
export interface StatsByPeriodPayload extends StatFilterPayload {
    period: 'day' | 'week' | 'month'
}

export const getStatsByPeriod = async (
    payload: StatsByPeriodPayload
): Promise<ResponseArray<PeriodStats>> => {
    const response = await datacentral.get<ResponseArray<PeriodStats>>(
        `/contacts/statistic/by-period?${qs.stringify(payload)}`
    )
    return response.data
}

/**
 * Get stats by product group
 * GET /contacts/statistic/by-product-group
 */
export type StatsByProductGroupPayload = StatFilterPayload

export const getStatsByProductGroup = async (
    payload?: StatsByProductGroupPayload
): Promise<ResponseArray<ProductGroupStats>> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ResponseArray<ProductGroupStats>>(
        `/contacts/statistic/by-product-group${queryString}`
    )
    return response.data
}

/**
 * Get stats by channel
 * GET /contacts/statistic/by-channel
 */
export type StatsByChannelPayload = StatFilterPayload

export const getStatsByChannel = async (
    payload?: StatsByChannelPayload
): Promise<ResponseArray<ChannelStats>> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ResponseArray<ChannelStats>>(
        `/contacts/statistic/by-channel${queryString}`
    )
    return response.data
}

/**
 * Get contacts with revenue
 * GET /contacts/with-revenue
 */
export interface ContactsWithRevenuePayload extends StatFilterPayload {
    page?: number
    page_size?: number
}

export interface ContactsWithRevenueResponse
    extends ResponseArray<ContactSource> {
    total_revenue: number
}

export const getContactsWithRevenue = async (
    payload?: ContactsWithRevenuePayload
): Promise<ContactsWithRevenueResponse> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ContactsWithRevenueResponse>(
        `/contacts/with-revenue${queryString}`
    )
    return response.data
}

/**
 * Get contacts with payment revenue (all paid invoices)
 * GET /contacts/with-payment-revenue
 */
export interface ContactsWithPaymentRevenuePayload extends StatFilterPayload {
    page?: number
    page_size?: number
    group_by_month?: string
}

export interface ContactsWithPaymentRevenueResponse
    extends ResponseArray<ContactSource> {
    total_revenue: number
}

export const getContactsWithPaymentRevenue = async (
    payload?: ContactsWithPaymentRevenuePayload
): Promise<ContactsWithPaymentRevenueResponse> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ContactsWithPaymentRevenueResponse>(
        `/contacts/with-payment-revenue${queryString}`
    )
    return response.data
}

// ============== ONCALL APIS ==============

export interface OncallSchedule {
    oncall_schedule_id: number
    oncall_schedule_name: string
    oncall_schedule_active: boolean
    oncall_shift_start_time: string
    oncall_shift_end_time: string
    oncall_rotation_frequency: 'daily' | 'weekly' | 'bi_weekly' | 'monthly'
    department_name: string
    department_id: number
    member_count?: number
}

/**
 * Get oncall schedules
 * GET /oncall/schedules
 */
export const getOncallSchedules = async (payload?: {
    department_id?: number
}): Promise<ResponseArray<OncallSchedule>> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ResponseArray<OncallSchedule>>(
        `/oncall/schedules${queryString}`
    )
    return response.data
}

export interface OncallAssignment {
    oncall_assignment_id: number
    oncall_schedule_id: number
    employee_id: number
    oncall_date: string
    oncall_start_time: string
    oncall_end_time: string
    oncall_assignment_status: 'scheduled' | 'active' | 'completed'
    original_employee_id: number | null
    reassignment_reason: string | null
    notification_sent: boolean
    handoff_notification_sent: boolean
    oncall_assignment_metadata: string | null
    created_by: number
    updated_by: number | null
    deleted_at: string | null
    created_at: string
    updated_at: string
    employee_name: string
    employee_email_company: string
    employee_phone_number: string
    employee_code: string
    oncall_schedule_name: string
    department_name: string
    schedule_department_id: number
    created_by_name: string
}

export interface OncallByDepartmentPayload {
    department_id: number
    start_date: string
    end_date: string
}

/**
 * Get oncall assignments by department (Sales = 2)
 * GET /oncalls/assignments/by-department
 */
export const getOncallByDepartment = async (
    payload: OncallByDepartmentPayload
): Promise<ResponseArray<OncallAssignment>> => {
    const response = await datacentral.get<ResponseArray<OncallAssignment>>(
        `/oncall/assignments/by-department?${qs.stringify(payload)}`
    )
    return response.data
}

/**
 * Get today's oncall assignments
 * GET /oncalls/assignments/today
 */
export const getTodayOncall = async (): Promise<
    ResponseArray<OncallAssignment>
> => {
    const response = await datacentral.get<ResponseArray<OncallAssignment>>(
        `/oncall/assignments/today`
    )
    return response.data
}

/**
 * Get oncall assignments list with pagination
 * GET /oncall/assignments
 */
export interface OncallAssignmentsListPayload {
    start_date: string
    end_date: string
    schedule_id?: number
    employee_id?: number
    status?: 'scheduled' | 'active' | 'completed'
    page?: number
    page_size?: number
}

export const getOncallAssignments = async (
    payload: OncallAssignmentsListPayload
): Promise<ResponseArray<OncallAssignment>> => {
    const response = await datacentral.get<ResponseArray<OncallAssignment>>(
        `/oncall/assignments?${qs.stringify(payload)}`
    )
    return response.data
}

/**
 * Generate oncall assignments for a schedule and date range
 * POST /oncall/assignments/generate
 */
export interface GenerateAssignmentsPayload {
    schedule_id: number
    start_date: string
    end_date: string
}

export interface GenerateAssignmentsResponse {
    created_count: number
    assignments: OncallAssignment[]
}

export const generateOncallAssignments = async (
    payload: GenerateAssignmentsPayload
): Promise<ResponseObject<GenerateAssignmentsResponse>> => {
    const response = await datacentral.post<
        ResponseObject<GenerateAssignmentsResponse>
    >(`/oncall/assignments/generate`, payload)
    return response.data
}

/**
 * Reassign an oncall assignment to another employee
 * PUT /oncall/assignments/:assignmentID/reassign
 */
export interface ReassignPayload {
    assignment_id: number
    new_employee_id: number
    reason?: string
}

export const reassignOncallAssignment = async (
    payload: ReassignPayload
): Promise<ResponseObject<OncallAssignment>> => {
    const response = await datacentral.put<ResponseObject<OncallAssignment>>(
        `/oncall/assignments/${payload.assignment_id}/reassign`,
        {
            new_employee_id: payload.new_employee_id,
            reason: payload.reason,
        }
    )
    return response.data
}
